import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReactMarkdown from 'react-markdown';

import { useAgent, useExecuteAgent, useProviderCatalog } from '../../../queries';
import { useAgentResultStore } from '../../../store/agentResultStore';
import { ApiResponseError } from '../../../api/envelope';
import { useRequireSignIn } from '../../../components/auth/useRequireSignIn';
import {
  ProviderModelSelect,
  defaultProviderAndModel,
} from '../../../components/ProviderModelSelect';

interface Props {
  agentId: string;
  /** Auto-populated into the Instruction textarea when the card opens.
   *  For Image Lab this is typically the active operation's prompt field. */
  defaultInstruction?: string;
  onClose: () => void;
}

/**
 * Inline expanded card that runs a single agent against a user instruction
 * and shows the result. Used by `AgentPanel` when a featured-agent card is
 * clicked. Bucket 3c of the Generic Studio punch list.
 *
 * - `defaultInstruction` seeds the textarea when the card opens so the
 *   common path ("enrich the prompt I'm composing") is a single click
 *   away from the result.
 * - "Use as prompt" pushes the result through `useAgentResultStore` so
 *   the active panel can consume it without prop drilling.
 */
export const AgentInvocationCard: React.FC<Props> = ({ agentId, defaultInstruction, onClose }) => {
  const { data: agent, isLoading: isAgentLoading, error: agentError } = useAgent(agentId);
  const [instruction, setInstruction] = useState(defaultInstruction ?? '');
  const execute = useExecuteAgent();
  const { requireSignIn } = useRequireSignIn();
  const setHandoff = useAgentResultStore(s => s.setHandoff);

  // Provider + model — user-selectable, seeded from the catalog with `gemini`
  // as preferred default (matches the BE's `DefaultModelFor("gemini")` path).
  const catalog = useProviderCatalog();
  const [provider, setProvider] = useState('');
  const [model, setModel] = useState('');
  useEffect(() => {
    if (provider || !catalog.data) return;
    const seed = defaultProviderAndModel(catalog.data.llm, 'gemini');
    setProvider(seed.provider);
    setModel(seed.model);
  }, [catalog.data, provider]);
  // When the user picks a new provider, swap model to that provider's first.
  const handleProviderChange = (next: string) => {
    setProvider(next);
    const entry = catalog.data?.llm.find(e => e.key === next);
    setModel(entry?.models[0]?.id ?? '');
  };
  // Tag for the result chip — explains which (provider, model) produced it.
  const usedTag = useMemo(() => {
    if (!execute.data?.success) return null;
    const ctx = execute.data.outputContext ?? {};
    const usedProvider = (ctx.provider as string | undefined) ?? provider;
    const usedModel = (ctx.model as string | undefined) ?? model;
    if (!usedProvider && !usedModel) return null;
    return `${usedProvider}${usedProvider && usedModel ? ' · ' : ''}${usedModel ?? ''}`;
  }, [execute.data, provider, model]);

  // If the parent re-opens this card with a new default (e.g. user typed
  // more into the panel prompt), refresh the seeded instruction — unless
  // the user has already edited it (instruction differs from default).
  useEffect(() => {
    if (defaultInstruction && instruction === '') {
      setInstruction(defaultInstruction);
    }
     
  }, [defaultInstruction]);

  const handleRun = async () => {
    const trimmed = instruction.trim();
    if (!trimmed) return;
    if (!requireSignIn()) return;
    await execute.mutateAsync({
      agentId,
      request: {
        requestId: crypto.randomUUID(),
        agentId,
        instruction: trimmed,
        context: {
          ...(provider ? { provider } : {}),
          ...(model ? { model } : {}),
        },
      },
    });
  };

  const handleUseAsPrompt = () => {
    const result = execute.data?.result?.trim();
    if (!result || !agent) return;
    setHandoff({
      agentId,
      agentName: agent.name,
      promptText: result,
    });
  };

  const apiError = execute.error instanceof ApiResponseError ? execute.error.message
    : execute.error instanceof Error ? execute.error.message
      : null;

  return (
    <Card variant="outlined" sx={{ borderColor: 'primary.light' }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {isAgentLoading && <Typography variant="body2">Loading agent…</Typography>}
            {agentError && (
              <Alert severity="error" variant="outlined" sx={{ py: 0 }}>
                Couldn't load agent.
              </Alert>
            )}
            {agent && (
              <>
                <Typography variant="body2" fontWeight={600}>
                  {agent.name}
                </Typography>
                {agent.description && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {agent.description}
                  </Typography>
                )}
              </>
            )}
          </Box>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} aria-label="Close agent card">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Box sx={{ mb: 1 }}>
          <ProviderModelSelect
            kind="llm"
            provider={provider}
            model={model}
            onProviderChange={handleProviderChange}
            onModelChange={setModel}
            disabled={execute.isPending}
            hideProviderWhenSingle
          />
        </Box>

        <TextField
          label="Instruction"
          value={instruction}
          onChange={e => setInstruction(e.target.value)}
          multiline
          minRows={2}
          maxRows={6}
          size="small"
          fullWidth
          disabled={execute.isPending}
          sx={{ mb: 1 }}
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            size="small"
            onClick={handleRun}
            disabled={execute.isPending || !instruction.trim()}
            startIcon={execute.isPending ? <CircularProgress size={14} /> : <PlayArrowIcon fontSize="small" />}
          >
            {execute.isPending ? 'Running…' : 'Run'}
          </Button>
          {execute.data?.success && execute.data.result && (
            <Tooltip title="Push this result into the active operation's prompt field">
              <Button
                variant="outlined"
                size="small"
                onClick={handleUseAsPrompt}
                startIcon={<ContentPasteGoIcon fontSize="small" />}
              >
                Use as prompt
              </Button>
            </Tooltip>
          )}
        </Stack>

        {apiError && (
          <Alert severity="error" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
            {apiError}
          </Alert>
        )}
        {execute.data && !execute.data.success && (
          <Alert severity="warning" variant="outlined" sx={{ mt: 1, py: 0.5 }}>
            {(execute.data.outputContext?.error as string) ?? 'Agent execution failed.'}
          </Alert>
        )}
        {execute.data?.success && execute.data.result && (
          <Box sx={{ mt: 1 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontSize: '0.875rem',
                '& p': { m: 0 },
                '& p + p': { mt: 1 },
              }}
            >
              <ReactMarkdown>{execute.data.result}</ReactMarkdown>
            </Box>
            {usedTag && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                via {usedTag}
              </Typography>
            )}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
